import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import './App.css';

// ── Eager: öffentliche Startseite ──────────────────────────────────
// Bewusst NICHT lazy — schnellster First Paint für Besucher der Landingpage.
import Header       from './components/Header';
import Hero         from './components/Hero';
import Products     from './components/Products';
import WhyTechSoul  from './components/WhyTechSoul';
import AppShowcase  from './components/AppShowcase';
import TrustSection from './components/TrustSection';
import PrivacySection from './components/PrivacySection';
import PrivacyFaq    from './components/PrivacyFaq';
import Services     from './components/Services';
import CoachingSection from './components/CoachingSection';
import About        from './components/About';
import Contact      from './components/Contact';
import Footer       from './components/Footer';
import GalaxyBackground from './components/GalaxyBackground';
import PwaInstallSection from './components/PwaInstallSection';

// ── Eager: Auth, Routen-Gates & globale Banner ─────────────────────
// Klein und überall gebraucht; die Gates liefern zudem die .auth-loading-Optik,
// die auch als Suspense-Fallback dient (admin.css landet dadurch im Kern-Bundle).
import { AuthProvider } from './auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import ErrorBoundary from './components/ErrorBoundary';
import RouteError from './components/RouteError';
import PageViewTracker from './components/PageViewTracker';
import FeatureRoute from './components/features/FeatureRoute';
import ModuleRoute from './components/auth/ModuleRoute';
import InstallBanner from './components/InstallBanner';
import UpdateBanner  from './components/UpdateBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import TermsModal from './components/TermsModal';
import { FEATURES }       from './features/featureRegistry';
import { useScrollReveal } from './hooks/useScrollReveal';

// ── Lazy: alles hinter Login + sekundäre öffentliche Seiten ────────
// Route-basiertes Code-Splitting (Audit-Finding H3): Besucher der Startseite laden
// nur noch den Kern-Chunk; Admin/Work/Life/Features kommen erst bei Navigation.

// Sekundäre öffentliche Seiten
const Privacy             = lazy(() => import('./components/Privacy'));
const Impressum           = lazy(() => import('./components/Impressum'));
const Preise              = lazy(() => import('./components/Preise'));
const Agb                 = lazy(() => import('./components/Agb'));
const Widerruf            = lazy(() => import('./components/Widerruf'));
const Register            = lazy(() => import('./components/Register'));
const CoachingLandingPage = lazy(() => import('./components/CoachingLandingPage'));
const CoachingBookingPage = lazy(() => import('./components/CoachingBookingPage'));
const PilotLandingPage    = lazy(() => import('./components/PilotLandingPage'));
const PwaLanding          = lazy(() => import('./components/PwaLanding'));
const AppEntry            = lazy(() => import('./components/AppEntry'));

// Admin
const AdminLogin      = lazy(() => import('./components/admin/AdminLogin'));
const AdminDashboard  = lazy(() => import('./components/admin/AdminDashboard'));
const UserList        = lazy(() => import('./components/admin/UserList'));
const UserFeatures    = lazy(() => import('./components/admin/UserFeatures'));
const InviteUser      = lazy(() => import('./components/admin/InviteUser'));
const ErrorLogs       = lazy(() => import('./components/admin/ErrorLogs'));
const TwoFASettings   = lazy(() => import('./components/admin/TwoFASettings'));
const ActivateInvite  = lazy(() => import('./components/admin/ActivateInvite'));
const IdeenManager          = lazy(() => import('./components/admin/IdeenManager'));
const FeedbackCenter        = lazy(() => import('./components/admin/FeedbackCenter'));
const BugReports            = lazy(() => import('./components/admin/BugReports'));
const ContentStudio         = lazy(() => import('./components/admin/ContentStudio'));
const CoachingAdminDashboard = lazy(() => import('./components/admin/CoachingAdminDashboard'));
const CoachingAvailability   = lazy(() => import('./components/admin/CoachingAvailability'));
const PilotApplications      = lazy(() => import('./components/admin/PilotApplications'));
const JobAlerts              = lazy(() => import('./components/admin/JobAlerts'));
const Problembehandlung     = lazy(() => import('./components/admin/Problembehandlung'));
const FragChristianAdmin    = lazy(() => import('./components/admin/FragChristianAdmin'));
const AdminFeaturesPage     = lazy(() => import('./components/admin/AdminFeaturesPage'));
const AdminStatistics       = lazy(() => import('./components/admin/AdminStatistics'));
const AdminVoiceSettings    = lazy(() => import('./components/admin/AdminVoiceSettings'));

// Work – Leadership Operating System
const WorkShell        = lazy(() => import('./components/work/WorkShell'));
const WorkToolPage     = lazy(() => import('./components/work/WorkToolPage'));
const WorkDashboard    = lazy(() => import('./components/work/WorkDashboard'));
const Werkzeuge        = lazy(() => import('./components/work/Werkzeuge'));
const Guide            = lazy(() => import('./components/work/Guide'));
const WorkCoachPage    = lazy(() => import('./components/work/WorkCoachPage'));
const MeetingHub            = lazy(() => import('./components/work/MeetingHub'));
const MemoryExplorer        = lazy(() => import('./components/work/MemoryExplorer'));
const MemoryModule          = lazy(() => import('./components/work/memory/MemoryModule'));
const TeamUebersicht        = lazy(() => import('./components/work/team/TeamUebersicht'));
// Named Exports brauchen bei React.lazy einen default-Wrapper
const AnalyticsPage = lazy(() => import('./components/work/WorkPlaceholders').then((m) => ({ default: m.AnalyticsPage })));

// Life – Personal OS
const LifeShell      = lazy(() => import('./components/life/LifeShell'));
const LifeDashboard  = lazy(() => import('./components/life/LifeDashboard'));
const LifeWerkzeuge  = lazy(() => import('./components/life/LifeWerkzeuge'));
const LifeCoachPage  = lazy(() => import('./components/life/LifeCoachPage'));
const LifeToolPage   = lazy(() => import('./components/life/LifeToolPage'));

// Feature-Seiten
const UserDashboard      = lazy(() => import('./components/features/UserDashboard'));
const BmiRechner         = lazy(() => import('./components/features/BmiRechner'));
const AudioTranskription = lazy(() => import('./components/features/AudioTranskription'));
const DiatRechner        = lazy(() => import('./components/features/DiatRechner'));
const SettingsPage       = lazy(() => import('./components/settings/SettingsPage'));
const SupportDeveloper   = lazy(() => import('./components/features/SupportDeveloper'));
const FragChristian      = lazy(() => import('./components/features/FragChristian'));
const MemorySearch       = lazy(() => import('./components/features/MemorySearch'));
const GedankenSortierer  = lazy(() => import('./components/features/clarity/GedankenSortierer'));
const VergessensChecker  = lazy(() => import('./components/features/clarity/VergessensChecker'));
const MeetingVorbereitung = lazy(() => import('./components/features/clarity/MeetingVorbereitung'));
const EntscheidungsHelfer = lazy(() => import('./components/features/clarity/EntscheidungsHelfer'));
const AntwortGenerator   = lazy(() => import('./components/features/clarity/AntwortGenerator'));
const SopGenerator       = lazy(() => import('./components/features/clarity/SopGenerator'));
const EinfachErklaerer   = lazy(() => import('./components/features/clarity/EinfachErklaerer'));
const ProblemAnalyzer    = lazy(() => import('./components/features/clarity/ProblemAnalyzer'));
const AufgabenUebergabe  = lazy(() => import('./components/features/AufgabenUebergabe'));
const PrivatBereich      = lazy(() => import('./components/features/PrivatBereich'));
const FuehrungsAkademie  = lazy(() => import('./components/features/FuehrungsAkademie'));
const RezeptGenerator    = lazy(() => import('./components/features/RezeptGenerator'));
const IdeenEinreichen    = lazy(() => import('./components/features/IdeenEinreichen'));
const VoiceCart          = lazy(() => import('./components/features/VoiceCart'));
const JobKompass         = lazy(() => import('./components/features/jobkompass/JobKompass'));
const TeamEventPlanung   = lazy(() => import('./components/features/teamEvent/TeamEventPlanung'));
const DokumentenPage     = lazy(() => import('./components/features/DokumentenPage'));
const DokumentDetail     = lazy(() => import('./components/features/DokumentDetail'));

const HomePage = () => {
  useScrollReveal();
  return (
    <>
      <GalaxyBackground />
      <Hero />
      <Products />
      <WhyTechSoul />
      <AppShowcase />
      <TrustSection />
      <PrivacySection />
      <PrivacyFaq />
      <PwaInstallSection />
      <About />
      <Services />
      <CoachingSection />
      <Contact />
    </>
  );
};

const router = createBrowserRouter([
  // Pathless Wurzel für errorElement: fängt ChunkLoadErrors aller Lazy-Routen
  // ab (Livetest-Punkt 16). Die Kind-Routen behalten ihre absoluten Pfade.
  // Seit v3.148.0 zusätzlich der PageViewTracker (rendert nur <Outlet/>), weil
  // er hier jeden Routenwechsel sieht — die cookielose Website-Statistik.
  {
    element: <PageViewTracker />,
    errorElement: <RouteError />,
    children: [
  // ── Public routes ──────────────────────────────────────────────
  {
    path: '/',
    element: (
      <div className="App">
        <div className="page">
          <Header />
          <HomePage />
          <Footer />
        </div>
      </div>
    ),
  },
  {
    path: '/datenschutz',
    element: (
      <div className="App">
        <div className="page">
          <Privacy />
          <Footer />
        </div>
      </div>
    ),
  },
  {
    path: '/impressum',
    element: (
      <div className="App">
        <div className="page">
          <Impressum />
          <Footer />
        </div>
      </div>
    ),
  },
  {
    path: '/preise',
    element: (
      <div className="App">
        <div className="page">
          <Preise />
          <Footer />
        </div>
      </div>
    ),
  },
  {
    path: '/agb',
    element: (
      <div className="App">
        <div className="page">
          <Agb />
          <Footer />
        </div>
      </div>
    ),
  },
  {
    path: '/coaching',
    element: (
      <div className="App">
        <div className="page">
          <CoachingLandingPage />
          <Footer />
        </div>
      </div>
    ),
  },
  {
    path: '/coaching/buchen',
    element: (
      <div className="App">
        <div className="page">
          <CoachingBookingPage />
          <Footer />
        </div>
      </div>
    ),
  },
  {
    // Pilotphase (v3.146.0): bewusst NICHT in Header/Startseite verlinkt — der
    // Einstieg läuft über den LinkedIn-Beitrag, die Gruppe ist auf 15–20 begrenzt.
    path: '/pilot',
    element: (
      <div className="App">
        <div className="page">
          <PilotLandingPage />
          <Footer />
        </div>
      </div>
    ),
  },
  {
    path: '/widerruf',
    element: (
      <div className="App">
        <div className="page">
          <Widerruf />
          <Footer />
        </div>
      </div>
    ),
  },

  // ── Auth routes ────────────────────────────────────────────────
  {
    path: '/admin/login',
    element: <AdminLogin />,
  },
  {
    path: '/login',
    element: <AdminLogin variant="user" />,
  },
  {
    path: '/registrieren',
    element: <Register />,
  },
  {
    path: '/activate-invite/:token',
    element: <ActivateInvite />,
  },

  // ── Protected admin routes (login required + admin claim) ──────
  {
    path: '/admin',
    element: (
      <AdminRoute>
        <AdminDashboard />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/users',
    element: (
      <AdminRoute>
        <UserList />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/users/invite',
    element: (
      <AdminRoute>
        <InviteUser />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/2fa',
    element: (
      <ProtectedRoute>
        <TwoFASettings />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/users/:uid/features',
    element: (
      <AdminRoute>
        <UserFeatures />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/error-logs',
    element: (
      <AdminRoute>
        <ErrorLogs />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/ideas',
    element: (
      <AdminRoute>
        <IdeenManager />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/clone',
    element: (
      <AdminRoute>
        <FragChristianAdmin />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/features',
    element: (
      <AdminRoute>
        <AdminFeaturesPage />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/statistics',
    element: (
      <AdminRoute>
        <AdminStatistics />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/feedback',
    element: (
      <AdminRoute>
        <FeedbackCenter />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/bug-reports',
    element: (
      <AdminRoute>
        <BugReports />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/content',
    element: (
      <AdminRoute>
        <ContentStudio />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/voice',
    element: (
      <AdminRoute>
        <AdminVoiceSettings />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/coaching',
    element: (
      <AdminRoute>
        <CoachingAdminDashboard />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/coaching/verfuegbarkeit',
    element: (
      <AdminRoute>
        <CoachingAvailability />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/pilot-bewerbungen',
    element: (
      <AdminRoute>
        <PilotApplications />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/stellenalarme',
    element: (
      <AdminRoute>
        <JobAlerts />
      </AdminRoute>
    ),
  },
  {
    path: '/admin/problembehandlung',
    element: (
      <AdminRoute>
        <Problembehandlung />
      </AdminRoute>
    ),
  },

  // ── User feature routes ────────────────────────────────────────
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <UserDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/documents',
    element: (
      <ProtectedRoute>
        <DokumentenPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/documents/:docId',
    element: (
      <ProtectedRoute>
        <DokumentDetail />
      </ProtectedRoute>
    ),
  },
  {
    // Alte Konto-Seite: seit v3.140.0 im Einstellungs-Hub aufgegangen.
    // Route bleibt als Umleitung, weil sie in alten Lesezeichen/Mails stecken kann.
    path: '/account',
    element: <Navigate to="/settings" replace />,
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <SettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/features/bmi',
    element: (
      <FeatureRoute featureId={FEATURES.BMI_RECHNER}>
        <BmiRechner />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/audio',
    element: (
      <FeatureRoute featureId={FEATURES.AUDIO_TRANSKRIPTION}>
        <AudioTranskription />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/diat-rechner',
    element: (
      <FeatureRoute featureId={FEATURES.DIAT_RECHNER}>
        <DiatRechner />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/support',
    element: (
      <FeatureRoute featureId={FEATURES.SUPPORT_DEVELOPER}>
        <SupportDeveloper />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/frag-christian',
    element: (
      <FeatureRoute featureId={FEATURES.FRAG_CHRISTIAN}>
        <FragChristian />
      </FeatureRoute>
    ),
  },

  // ── KI-Werkzeuge (einzeln aktivierbar) ───────────────────────
  {
    path: '/features/gedanken-sortierer',
    element: (
      <FeatureRoute featureId={FEATURES.GEDANKEN_SORTIERER}>
        <GedankenSortierer />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/vergessens-checker',
    element: (
      <FeatureRoute featureId={FEATURES.VERGESSENS_CHECKER}>
        <VergessensChecker />
      </FeatureRoute>
    ),
  },
  {
    // Standalone-Tool in den Meeting-Hub überführt (v3.102.0) – alte Links leiten dorthin um
    path: '/features/meeting-analyse',
    element: <Navigate to="/work/meetings" replace />,
  },
  {
    path: '/features/meeting-vorbereitung',
    element: (
      <FeatureRoute featureId={FEATURES.MEETING_VORBEREITUNG}>
        <MeetingVorbereitung />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/entscheidungs-helfer',
    element: (
      <FeatureRoute featureId={FEATURES.ENTSCHEIDUNGS_HELFER}>
        <EntscheidungsHelfer />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/antwort-generator',
    element: (
      <FeatureRoute featureId={FEATURES.ANTWORT_GENERATOR}>
        <AntwortGenerator />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/sop-generator',
    element: (
      <FeatureRoute featureId={FEATURES.SOP_GENERATOR}>
        <SopGenerator />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/einfach-erklaerer',
    element: (
      <FeatureRoute featureId={FEATURES.EINFACH_ERKLAERER}>
        <EinfachErklaerer />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/aufgaben-uebergabe',
    element: (
      <FeatureRoute featureId={FEATURES.AUFGABEN_UEBERGABE}>
        <AufgabenUebergabe />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/privat',
    element: (
      <FeatureRoute featureId={FEATURES.PRIVAT_BEREICH}>
        <PrivatBereich />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/akademie',
    element: (
      <FeatureRoute featureId={FEATURES.AKADEMIE_ZUGANG}>
        <FuehrungsAkademie />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/rezept-generator',
    element: (
      <FeatureRoute featureId={FEATURES.REZEPT_GENERATOR}>
        <RezeptGenerator />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/problem-analyzer',
    element: (
      <FeatureRoute featureId={FEATURES.PROBLEM_ANALYZER}>
        <ProblemAnalyzer />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/voice-cart',
    element: (
      <FeatureRoute featureId={FEATURES.VOICE_CART}>
        <VoiceCart />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/ideen-einreichen',
    element: (
      <FeatureRoute featureId={FEATURES.IDEEN_EINREICHEN}>
        <IdeenEinreichen />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/memory',
    element: (
      <FeatureRoute featureId={FEATURES.MEMORY}>
        <MemorySearch />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/job-kompass',
    element: (
      <FeatureRoute featureId={FEATURES.JOB_KOMPASS}>
        <JobKompass />
      </FeatureRoute>
    ),
  },
  {
    path: '/features/team-event-planung',
    element: (
      <FeatureRoute featureId={FEATURES.TEAM_EVENT_PLANUNG}>
        <TeamEventPlanung />
      </FeatureRoute>
    ),
  },

  // ── Work – Leadership Operating System (Layout-Route mit Outlet) ─
  {
    path: '/work',
    element: <ProtectedRoute><WorkShell /></ProtectedRoute>,
    children: [
      { index: true,           element: <WorkDashboard /> },
      { path: 'werkzeuge',     element: <Werkzeuge /> },
      // Guide: rein lesende Bedienungshilfe, Grundausstattung (kein ModuleRoute) —
      // die Modul-Kacheln filtern sich intern über hasModule (IdeenManager „minneapolis")
      { path: 'guide',         element: <Guide /> },
      { path: 'delegation',    element: <Navigate to="/work/tool/aufgaben_uebergabe" replace /> },
      { path: 'meetings',      element: <ModuleRoute moduleKey="meetings"><MeetingHub /></ModuleRoute> },
      { path: 'team',          element: <ModuleRoute moduleKey="team_leadership"><TeamUebersicht /></ModuleRoute> },
      { path: 'analytics',     element: <AnalyticsPage /> },
      // Coach als volle Seite IN der Shell (Pendant zu /life/coach). Vorher zeigte
      // die BottomNav auf /features/frag-christian und warf den Nutzer damit aus
      // der Shell. WorkShell blendet sein Seitenpanel auf dieser Route aus.
      { path: 'coach',         element: <WorkCoachPage /> },
      { path: 'gedaechtnis',       element: <MemoryExplorer embedded /> },
      // TechSoul-Memory-Hub (v3.101.0): ein Zuhause für Suche/Kunden/Projekte/Entscheidungen/Prozesse/Coach.
      // Kein ModuleRoute — der Hub ist immer erreichbar, Gating passiert pro Reiter (MemoryModule).
      { path: 'memory',            element: <MemoryModule embedded /> },
      { path: 'wissen',            element: <Navigate to="/work/memory?tab=suche" replace /> },
      { path: 'entscheidungen',    element: <Navigate to="/work/memory?tab=entscheidungen" replace /> },
      { path: 'prozesse',          element: <Navigate to="/work/memory?tab=prozesse" replace /> },
      { path: 'einstellungen',     element: <SettingsPage embedded /> },
      // Dokumente ist Grundausstattung (kein ModuleRoute) und hängt unter BEIDEN
      // Shells — beide Bereiche speichern dorthin. /documents bleibt zusätzlich
      // top-level für den UserHeader-Bereich bestehen (Livetest-Punkt 12).
      { path: 'dokumente',         element: <DokumentenPage embedded /> },
      { path: 'dokumente/:docId',  element: <DokumentDetail embedded /> },
      { path: 'tool/:featureId',   element: <WorkToolPage /> },
    ],
  },

  // ── Life – Personal OS (Layout-Route mit Outlet) ───────────
  {
    path: '/life',
    element: <ProtectedRoute><LifeShell /></ProtectedRoute>,
    children: [
      { index: true,          element: <LifeDashboard /> },
      { path: 'ziele',        element: <LifeDashboard /> },
      { path: 'werkzeuge',    element: <LifeWerkzeuge /> },
      { path: 'coach',        element: <LifeCoachPage /> },
      { path: 'einstellungen', element: <SettingsPage embedded /> },
      { path: 'dokumente',        element: <DokumentenPage embedded /> },
      { path: 'dokumente/:docId', element: <DokumentDetail embedded /> },
      { path: 'tool/:featureId', element: <LifeToolPage /> },
    ],
  },

  // ── PWA-Einstieg ───────────────────────────────────────────────
  // start_url des Standard-Manifests. Leitet in den zuletzt genutzten Bereich
  // statt auf die Marketing-Webseite; die Webseite unter "/" bleibt unverändert.
  {
    path: '/app',
    element: <AppEntry />,
  },

  // ── PWA install landing pages ──────────────────────────────────
  {
    path: '/app/work',
    element: <PwaLanding variant="work" />,
  },
  {
    path: '/app/life',
    element: <PwaLanding variant="life" />,
  },
    ],
  },
]);

// Fallback, während ein nachgeladener Routen-Chunk lädt (Code-Splitting).
// Gleiche Optik wie die Auth-Ladeanzeige der Routen-Gates. Bei In-App-Navigation
// hält React Router (startTransition) die alte Seite sichtbar — der Fallback
// erscheint praktisch nur beim Direktaufruf einer Lazy-Route per URL.
function ChunkLoader() {
  return (
    <div className="auth-loading">
      <div className="auth-loading__spinner" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ImpersonationBanner />
        <InstallBanner />
        <UpdateBanner />
        <TermsModal />
        <Suspense fallback={<ChunkLoader />}>
          <RouterProvider router={router} />
        </Suspense>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
